import { useMutation } from '@tanstack/react-query'
import React, { createContext, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Navigate, Outlet, RouteObject, useNavigate, useParams } from 'react-router-dom'
import { useConfirmationDialog } from 'src/hooks/useConfirmationDialog'
import { HttpMessages } from 'src/http/workflow/httpMessages'
import { EntityWithIdAndDescription, EntityWithIdAndDescriptionFields, EntityWithIdFields } from 'src/types'
import { MensajeDetalle, MensajeDetalleFields } from '../messages/types'

import { LoaderProvider } from 'src/context/LoaderContext'
import { useLoader } from 'src/hooks/useLoader'
import ItaptecSpinner from "src/layouts/components/ItaptecSpinner"
import { z } from 'src/lib'
import { PermissionType } from 'src/types/security'
import { SistemaTipo } from "../../types/enum/enum"
import { downloadFileBlobHelper } from "../../utils/helpers"
import { NuevoSistema } from './NuevoSistema'
import { useAcciones } from './hooks/useAcciones'
import { useEjecutarAccion } from './hooks/useEjecutarAccion'
import OperacionLayout from './layout/OperacionLayout'
import { Comentarios } from './sistemas/Comentarios'
import { Documentacion } from './sistemas/Documentacion'
import SistemaADCM from './sistemas/adcm/SistemaADCM'
import { HttpADCM } from './sistemas/adcm/api/httpADCM'
import SistemaADL from './sistemas/adl/SistemaADL'
import { HttpADL } from './sistemas/adl/api/httpADL'
import SistemaAFOCL from './sistemas/afocl/SistemaAFOCL'
import { HttpAFOCL } from './sistemas/afocl/api/httoAFOCL'
import SistemaAFOPU from './sistemas/afopu/SistemaAFOPU'
import { HttpAFOPU } from './sistemas/afopu/api/httoAFOPU'
import SistemaCapitalMarkets from "./sistemas/capital_markets/SistemaCapitalMarkets"
import { HttpCapitalMarkets } from "./sistemas/capital_markets/api/httpCapitalMarkets"
import SistemaCF from './sistemas/cf/SistemaCF'
import { HttpCF } from './sistemas/cf/api/httpCF'
import SistemaCLFB from "./sistemas/clfb/SistemaCLFB"
import { HttpCLFB } from "./sistemas/clfb/api/HttpCLFB"
import SistemaCLGarantia from "./sistemas/clg/SistemaCLGarantia"
import { HttpCLG } from "./sistemas/clg/api/HttpCLG"
import { SistemaContraparte } from './sistemas/contraparte/SistemaContraparte'
import { HttpContraparte } from "./sistemas/contraparte/api/httpContraparte"
import SistemaControlOperativo from "./sistemas/control-operativo/SistemaControlOperativo"
import { HttpControlOperativo } from "./sistemas/control-operativo/api/httpControlOperativo"
import { SistemaCorporate } from "./sistemas/corporate/SistemaCorporate"
import { HttpCorporate } from "./sistemas/corporate/api/httpCorporate"
import SistemaCorporateLending from "./sistemas/corporate_lending/SistemaCorporateLending"
import { HttpCorporateLending } from "./sistemas/corporate_lending/api/httpCorporateLending"
import SistemaLCLS from "./sistemas/lcls/SistemaLCLS"
import { HttpLCLS } from "./sistemas/lcls/api/httpLCLS"
import SistemaLCSLHPTI from './sistemas/lcslhpti/SistemaLCSLHPTI'
import { HttpLCSLHPTI } from './sistemas/lcslhpti/api/httpLCSLSHPTI'
import SistemaLCTC from "./sistemas/lctc/SistemaLCTC"
import { HttpLCTC } from "./sistemas/lctc/api/httpLCTC"
import SistemaLEP from "./sistemas/lep/SistemaLEP"
import { HttpLEP } from './sistemas/lep/api/httpLEP'
import { SistemaNotaEstructurada } from "./sistemas/nota_estructurada/SistemaNotaEstructurada"
import { HttpNotaEstructurada } from "./sistemas/nota_estructurada/api/httpNotaEstructurada"
import SistemaNOTES from './sistemas/notes/SistemaNOTES'
import { HttpNOTES } from './sistemas/notes/api/httpNOTES'
import SistemaTransferenciaTerceros from "./sistemas/transferencia-terceros/SistemaTransferenciaTerceros"
import { HttpTransferenciaTerceros } from "./sistemas/transferencia-terceros/api/httpTransferenciaTerceros"
import { SistemaWRCredito } from './sistemas/wrc/SistemaWRCredito'
import { HttpWRC } from "./sistemas/wrc/api"
import { SistemaWRMercado } from './sistemas/wrm'
import { HttpWRM } from "./sistemas/wrm/api"


type SistemaConfiguracion = {
    path: string;
    Component: React.ComponentType;
    descargarInforme: (idOperacion: number) => Promise<any>;
};

const SISTEMAS_CONFIG: Record<SistemaTipo, SistemaConfiguracion> = {
    [SistemaTipo.MinutaCredito]: {
        path: 'wrc',
        Component: SistemaWRCredito,
        descargarInforme: (id) => HttpWRC.descargarInforme(id),
    },
    [SistemaTipo.MinutaMercado]: {
        path: 'wrm',
        Component: SistemaWRMercado,
        descargarInforme: (id) => HttpWRM.descargarInforme(id),
    },
    [SistemaTipo.Contrapartes]: {
        path: 'contrapartes',
        Component: SistemaContraparte,
        descargarInforme: (id) => HttpContraparte.descargarInforme(id),
    },
    [SistemaTipo.CLG]: {
        path: 'clg',
        Component: SistemaCLGarantia,
        descargarInforme: (id) => HttpCLG.descargarInforme(id),
    },
    [SistemaTipo.CLFB]: {
        path: 'clfb',
        Component: SistemaCLFB,
        descargarInforme: (id) => HttpCLFB.descargarInforme(id),
    },
    [SistemaTipo.Corporate]: {
        path: 'corporate',
        Component: SistemaCorporate,
        descargarInforme: (id) => HttpCorporate.descargarInforme(id),
    },
    [SistemaTipo.CapitalMarkets]: {
        path: 'capital-markets',
        Component: SistemaCapitalMarkets,
        descargarInforme: (id) => HttpCapitalMarkets.descargarInforme(id),
    },
    [SistemaTipo.NotaEstructurada]: {
        path: 'nota-estructurada',
        Component: SistemaNotaEstructurada,
        descargarInforme: (id) => HttpNotaEstructurada.descargarInforme(id),
    },
    [SistemaTipo.TransferenciaTerceros]: {
        path: 'transferencia-terceros',
        Component: SistemaTransferenciaTerceros,
        descargarInforme: (id) => HttpTransferenciaTerceros.descargarInforme(id),
    },
    [SistemaTipo.CorporateLending]: {
        path: 'corporate-lending',
        Component: SistemaCorporateLending,
        descargarInforme: (id) => HttpCorporateLending.descargarInforme(id),
    },
    [SistemaTipo.ControlOperativo]: {
        path: 'control-operativo',
        Component: SistemaControlOperativo,
        descargarInforme: (id) => HttpControlOperativo.descargarInforme(id),
    },
    [SistemaTipo.LEP]: {
        path: 'lep',
        Component: SistemaLEP,
        descargarInforme: (id) => HttpLEP.descargarInforme(id),
    },
    [SistemaTipo.LCLS]: {
        path: 'lcls',
        Component: SistemaLCLS,
        descargarInforme: (id) => HttpLCLS.descargarInforme(id),
    },
    [SistemaTipo.LCTC]: {
        path: 'lctc',
        Component: SistemaLCTC,
        descargarInforme: (id) => HttpLCTC.descargarInforme(id),
    },
    [SistemaTipo.AFOPU]: {
        path: 'afopu',
        Component: SistemaAFOPU,
        descargarInforme: (id) => HttpAFOPU.descargarInforme(id),
    },
    [SistemaTipo.AFOCL]: {
        path: 'afocl',
        Component: SistemaAFOCL,
        descargarInforme: (id) => HttpAFOCL.descargarInforme(id),
    },
    [SistemaTipo.LCSLHPTI]: {
        path: 'lcslhpti',
        Component: SistemaLCSLHPTI,
        descargarInforme: (id) => HttpLCSLHPTI.descargarInforme(id),
    },
    [SistemaTipo.NOTES]: {
        path: 'notes',
        Component: SistemaNOTES,
        descargarInforme: (id) => HttpNOTES.descargarInforme(id),
    },
    [SistemaTipo.ADCM]: {
        path: 'adcm',
        Component: SistemaADCM,
        descargarInforme: (id) => HttpADCM.descargarInforme(id),
    },
    [SistemaTipo.ADL]: {
        path: 'adl',
        Component: SistemaADL,
        descargarInforme: (id) => HttpADL.descargarInforme(id),
    },
    [SistemaTipo.CF]: {
        path: 'cf',
        Component: SistemaCF,
        descargarInforme: (id) => HttpCF.descargarInforme(id),
    }
};

// mapea la configuracion a esto: [SistemaTipo.CF]: 'cf'
export const rutaPorCodSistema = Object.fromEntries(
    Object.entries(SISTEMAS_CONFIG).map(([key, value]) => [key, value.path])
) as Record<SistemaTipo, string>;

const urlSchemaIdMensaje = z.object({
    idMensaje: z.coerce.number()
})

const cargarMensaje = (parametros: unknown, codSistema: SistemaTipo): Promise<MensajeDetalle> => {

    const parsed = urlSchemaIdMensaje.safeParse(parametros)

    if (!parsed.success) {
        return Promise.reject('Id mensaje inválido.')
    }

    return HttpMessages.getDetail(parsed.data.idMensaje)
        .then(value => {
            if (value[MensajeDetalleFields.CodSistemaTipo] === codSistema) {
                return Promise.resolve(value)
            } else {
                return Promise.reject('Error grave: este mensaje no pertenece a este sistema.')
            }
        })
        .catch(e => {
            console.log('error al cargar mensaje: ', e)

            return Promise.reject(e) // TODO: acá validar el tipo del error, no autorizado / no existe, etc
        })
}


// # region nuevo
// ESTO SIRVE PARA EL NUEVO

// const urlParamsNuevaOperacionSchema = z.object({
//     codSistema: z.coerce.number(),          // TODO: agregar que esté dentro de los posibles sistemas con z.enum
//     codSistemaPrincipal: z.coerce.number()  // TODO: idem arriba
// })

// type UrlParamsNuevaOperacion = z.infer<typeof urlParamsNuevaOperacionSchema>

// const cargarParametros: LoaderFunction = ({ request, params }): Promise<UrlParamsNuevaOperacion> => {
//     const url = new URL(request.url);
//     const stringSistemaPrincipal = url.searchParams.get(PROJECT_CONFIG.alias.sistemaPrincipal)  // nombre pais
//     const stringSistema = url.searchParams.get(PROJECT_CONFIG.alias.sistema)                    // nombre operacion

//     // parsear, validar y devolver codSistemaPrincipal y codSistema

//     // TODO: esto obviamente está hardcodeado
//     return Promise.resolve({
//         codSistema: 1,
//         codSistemaPrincipal: 1
//     })
// }
// # endregion

const WrapperPaginaOperacion = ({codSistema}: { codSistema: SistemaTipo }) => {

    // const loaderData = useLoaderData()

    // console.log(loaderData)

    const [mensaje, setMensaje] = useState<MensajeDetalle>()

    const params = useParams()

    const navigation = useNavigate()

    useEffect(() => {
        cargarMensaje(params, codSistema)
            .then(msj => setMensaje(msj))

            .catch(() => navigation('/home'))
    }, [])

    if (!mensaje) {

        return <ItaptecSpinner/> // TODO: esto no deberías ser 404, sino 400 (bad request)
    }

    return (
        <PaginaOperacion mensaje={mensaje}>
            <Outlet/>
        </PaginaOperacion>
    )
}

export type ContextOperacionType = {
    setSavePromise: React.Dispatch<React.SetStateAction<(() => void) | undefined>>
    setPromiseInformeContent?: React.Dispatch<React.SetStateAction<(() => Promise<JSX.Element>) | undefined>>
    idOperacion: number
    codSistemaTipo: number
    codSistemaPrincipal: number,
    codSistema: number,
    permisoTipo: PermissionType,
}


export const ContextOperacion = createContext<ContextOperacionType>({
    setSavePromise: console.log,
    idOperacion: 0,
    codSistemaTipo: 0,
    codSistemaPrincipal: 0,
    codSistema: 0,
    permisoTipo: PermissionType.None
});


const getInformePromise = (mensaje: MensajeDetalle): Promise<any> => {
    const codSistema = mensaje[MensajeDetalleFields.CodSistemaTipo] as SistemaTipo;
    const sistemaConfig = SISTEMAS_CONFIG[codSistema];

    if (!sistemaConfig) {
        return Promise.reject(`Error: sistema ${codSistema} no reconocido.`);
    }

    return sistemaConfig.descargarInforme(mensaje[MensajeDetalleFields.IdOperacion]);
};

const PaginaOperacion = ({mensaje, children}: { mensaje: MensajeDetalle, children: React.ReactNode }) => {

    const [promiseInformeContent, setPromiseInformeContent] = useState<() => Promise<JSX.Element>>()

    const [savePromise, setSavePromise] = useState<() => void>()   // TODO: funciones default para que

    const navigation = useNavigate()
    const {showLoader, hideLoader} = useLoader()
    const {askConfirmation} = useConfirmationDialog()

    const {acciones} = useAcciones(mensaje[MensajeDetalleFields.IdEtapaActual])
    const {onClickEjecutar} = useEjecutarAccion(mensaje[EntityWithIdFields.Id])

    const descargaInforme = useMutation({
        mutationFn: () => {
            showLoader()

            return getInformePromise(mensaje);
        },
        onSuccess: (res) => {
            hideLoader()
            downloadFileBlobHelper(res)
        },
        onError: () => {
            hideLoader()
        },
    });


    // TODO: mejorar las promesas para que tengan mensaje de error. Mejorar el tipo.
    const handleEnviar = (seleccion: EntityWithIdAndDescription) => {
        showLoader()
        const promise = onClickEjecutar({
            idAccion: seleccion[EntityWithIdAndDescriptionFields.Id]
        })

        toast.promise(promise, {
            loading: 'Enviando.',
            success: 'Enviado correctamente.',
            error: e => e
        })
            .then(handleCloseMessage)
            .catch(hideLoader)
    }

    const handleCloseMessage = () => {
        showLoader();
        const promise = HttpMessages.closeMessage(mensaje[EntityWithIdFields.Id])

        toast.promise(promise, {
            loading: 'Cerrando.',
            success: 'Cerrado.',
            error: 'Error al cerrar'
        })
            .then(() => {
                setTimeout(() => navigation('/'), 500)

            })
            .catch(() => {
                hideLoader()
                toast.error("Error al cerrar.")
            })
    }

    const executeCancelMessage = () => {
        showLoader();
        const promise = HttpMessages.cancelMessage(mensaje[EntityWithIdFields.Id])

        toast.promise(promise, {
            loading: 'Anulando.',
            success: 'Anulado.',
            error: 'Error al anular'
        })
            .then(() => {
                setTimeout(() => navigation('/'), 500)

            })
            .catch(() => {
                hideLoader()
                toast.error("Error al anular.")
            })
    }

    const handleCancelMessage = () => {
        askConfirmation(
            executeCancelMessage,
            `Esta seguro que desea anular el mensaje ${mensaje[EntityWithIdFields.Id]}?`
        )
    }

    const handleDescargarInforme = () => {
        const promise = descargaInforme.mutateAsync()
        toast.promise(promise, {
            loading: 'Generando informe.',
            success: 'Informe listo para descarga.',
            error: e => `${e}`
        })
    }

    const [openPrintDialog, setOpenPrintDialog] = useState<boolean>(false)

    return (
        <LoaderProvider>
            <OperacionLayout

                acciones={{
                    isNewOperacion: false,
                    saveHandler: savePromise,
                    closeHandler: handleCloseMessage,
                    cancelHandler: handleCancelMessage,

                    // Deshabilitada descarga para todos los wf
                    // printHandler: (handleDescargarInforme),

                    actionsOptions: mensaje[MensajeDetalleFields.PermisoTipo] > 1 ? acciones : [],
                    actionsHandler: handleEnviar
                }}
                message={mensaje}
            >
                <ContextOperacion.Provider value={{
                    setSavePromise,
                    setPromiseInformeContent,
                    idOperacion: mensaje[MensajeDetalleFields.IdOperacion],
                    codSistemaTipo: mensaje[MensajeDetalleFields.CodSistemaTipo],
                    codSistemaPrincipal: mensaje[MensajeDetalleFields.CodigoSistemaPrincipal],
                    codSistema: mensaje[MensajeDetalleFields.CodigoSistema],
                    permisoTipo: mensaje[MensajeDetalleFields.PermisoTipo]
                }}>
                    {children}
                </ContextOperacion.Provider>
            </OperacionLayout>
        </LoaderProvider>
    )
}

// generamos las rutas y los componentes en base a la config
const rutasDeOperaciones = Object.entries(SISTEMAS_CONFIG).map(([codSistema, config]) => ({
    children: [
        {
            path: `${config.path}/:idMensaje`,
            element: <WrapperPaginaOperacion codSistema={Number(codSistema) as SistemaTipo} />,
            children: [
                {
                    path: '',
                    element: <config.Component />
                },
                {
                    path: 'documentos',
                    element: <Documentacion />
                },
                {
                    path: 'comentarios',
                    element: <Comentarios />
                }
            ]
        }
    ]
}));


export const sistemaRouter: RouteObject[] = [
    {
        path: '/operacion',
        element: <Outlet />,
        children: [
            ...rutasDeOperaciones,
            { path: '', element: <Navigate to='/home' /> }
        ],
    },
    {
        path: '/operacion/nuevo',
        element: <NuevoSistema />
    }
];

// export const sistemaRouter: RouteObject[] = [
//     {
//         path: '/operacion',
//         element: <Outlet/>,
//         children: [
//             rutasDeOperaciones[SistemaTipo.MinutaCredito],
//             rutasDeOperaciones[SistemaTipo.MinutaMercado],
//             rutasDeOperaciones[SistemaTipo.Contrapartes],
//             rutasDeOperaciones[SistemaTipo.CLG],
//             rutasDeOperaciones[SistemaTipo.CLFB],
//             rutasDeOperaciones[SistemaTipo.Corporate],
//             rutasDeOperaciones[SistemaTipo.CapitalMarkets],
//             rutasDeOperaciones[SistemaTipo.NotaEstructurada],
//             rutasDeOperaciones[SistemaTipo.TransferenciaTerceros],
//             rutasDeOperaciones[SistemaTipo.CorporateLending],
//             rutasDeOperaciones[SistemaTipo.ControlOperativo],
//             rutasDeOperaciones[SistemaTipo.LEP],
//             rutasDeOperaciones[SistemaTipo.LCLS],
//             rutasDeOperaciones[SistemaTipo.LCTC],
//             rutasDeOperaciones[SistemaTipo.AFOPU],
//             rutasDeOperaciones[SistemaTipo.AFOCL],
//             rutasDeOperaciones[SistemaTipo.LCSLHPTI],
//             rutasDeOperaciones[SistemaTipo.NOTES],
//             rutasDeOperaciones[SistemaTipo.ADCM],
//             rutasDeOperaciones[SistemaTipo.ADL],
//             rutasDeOperaciones[SistemaTipo.CF],
            
//             {path: '', element: <Navigate to='/home'/>}
//         ],
//     },
//     {
//         path: '/operacion/nuevo',
//         element: <NuevoSistema/>   // TODO: acá en nuevo agregar el dialog y eso así se puede usar también
//     }
// ]


